import React, { useState, useEffect, useMemo, useRef } from "react";
import { Modal, Button, Table, Form, Badge, InputGroup, Alert, Row, Col, Card, ProgressBar, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaCheck, FaBarcode, FaBoxOpen, FaTrash, FaPlus, FaBatteryFull, FaExclamationTriangle, FaTimes, FaCube } from "react-icons/fa";
import { BiScan, BiBarcodeReader } from "react-icons/bi";
import { toast } from "react-toastify";
import apiService from "../../services/apiServices";
import { formatCurrency } from "../../utility";
import ImageUploader from "../core/components/ImageUploader/ImageUploader";

// --- THEME ---
const BRAND_COLOR = "#001c3a";

// --- CONSTANTS ---
const CONDITION_LIST = [
    "Brand New",
    "Like New",
    "Open Box",
    "Used Grade A",
    "Used Grade B",
    "Used",
    "Pre-Owned",
    "Refurbished",
    "Active",
    "Non-Active",
    "Damaged"
];

// --- SOUND EFFECTS ---
const playSound = (type) => {
    if (type === 'error') console.log("BZZZT!");
    if (type === 'success') console.log("BEEP!");
};

const PurchaseVerification = ({ show, onHide, purchase, onSuccess }) => {
    // --- STATE ---
    const [items, setItems] = useState([]);
    const [verificationNotes, setVerificationNotes] = useState("");
    const [supplierBillNo, setSupplierBillNo] = useState("");
    const [billProof, setBillProof] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Scanning State
    const [activeScanIdx, setActiveScanIdx] = useState(null);
    const [scanInput, setScanInput] = useState("");
    const scanInputRef = useRef(null);

    // --- INITIALIZATION & DATA NORMALIZATION ---
    useEffect(() => {
        if (purchase && purchase.purchasedItems) {
            const initialItems = purchase.purchasedItems.map((item) => {
                // FORENSIC DATA RECOVERY: Priority to Variant Name, then Base Item
                let safeName = item.itemName || "Unknown Item"; // Default to snapshot
                let safeCategory = item.category || "General";

                // If Base Item is populated, grab defaults from there
                if (item.item_id && typeof item.item_id === 'object') {
                    safeName = item.item_id.itemName || safeName;
                    safeCategory = item.item_id.category || safeCategory;
                }

                // IF Variant ID exists and is populated, OVERRIDE with Variant Name
                if (item.variant_id && typeof item.variant_id === 'object') {
                    safeName = item.variant_id.variantName || safeName;
                }

                return {
                    ...item,
                    // Normalize these fields to top-level for easier UI rendering
                    itemName: safeName,
                    category: safeCategory,

                    verifiedQty: item.isSerialized ? (item.serializedItems?.length || 0) : 0,
                    serializedItems: item.serializedItems?.map(s => ({
                        ...s,
                        verified: true,
                        // Ensure legacy data has defaults
                        batteryHealth: s.batteryHealth || (s.condition === 'Brand New' ? 100 : ''),
                        condition: s.condition || "Brand New"
                    })) || [],
                    status: "Pending"
                };
            });
            setItems(initialItems);

            // Auto-select first pending item
            const firstPending = initialItems.findIndex(i => i.verifiedQty < i.purchaseQty);
            if (firstPending !== -1) setActiveScanIdx(firstPending);
        }
    }, [purchase, show]);

    // --- FOCUS MANAGEMENT ---
    useEffect(() => {
        if (show && scanInputRef.current) {
            scanInputRef.current.focus();
        }
    }, [show, activeScanIdx]);

    // --- COMPUTED STATS ---
    const stats = useMemo(() => {
        const totalItems = items.reduce((acc, i) => acc + (i.purchaseQty || 0), 0);
        const verifiedItems = items.reduce((acc, i) => acc + (i.verifiedQty || 0), 0);
        const progress = totalItems === 0 ? 0 : Math.round((verifiedItems / totalItems) * 100);
        return { totalItems, verifiedItems, progress };
    }, [items]);

    // --- HANDLERS ---
    const handleGlobalScan = (e) => {
        e.preventDefault();
        const serial = scanInput.trim();
        if (!serial) return;

        if (activeScanIdx === null) {
            playSound('error');
            toast.warning("Please select a line item to associate this scan.");
            return;
        }
        handleAddSerial(activeScanIdx, serial);
        setScanInput("");
    };

    const handleAddSerial = (itemIdx, serial = "") => {
        const newItems = [...items];
        const item = newItems[itemIdx];

        if (item.serializedItems.length >= item.purchaseQty) {
            playSound('error');
            toast.warning(`Quantity limit (${item.purchaseQty}) reached.`);
            return;
        }

        if (item.serializedItems.some(s => s.serialNumber === serial && serial !== "")) {
            playSound('error');
            toast.error("Duplicate: Serial already scanned.");
            return;
        }

        item.serializedItems.push({
            serialNumber: serial,
            unitCost: item.unitCost,
            sellingPrice: item.sellingPrice,
            condition: "Brand New",
            batteryHealth: 100, // Default for new entry
            verified: true
        });

        item.verifiedQty = item.serializedItems.length;
        item.status = item.verifiedQty === item.purchaseQty ? "Verified" : "Pending";

        setItems(newItems);
        playSound('success');

        if (item.verifiedQty === item.purchaseQty) {
            const nextIdx = newItems.findIndex((i, idx) => idx > itemIdx && i.verifiedQty < i.purchaseQty);
            if (nextIdx !== -1) setActiveScanIdx(nextIdx);
        }
    };

    const handleRemoveSerial = (itemIdx, snIdx) => {
        const newItems = [...items];
        newItems[itemIdx].serializedItems.splice(snIdx, 1);
        newItems[itemIdx].verifiedQty = newItems[itemIdx].serializedItems.length;
        newItems[itemIdx].status = "Pending";
        setItems(newItems);
    };

    const handleUnitChange = (itemIdx, snIdx, field, val) => {
        const newItems = [...items];
        newItems[itemIdx].serializedItems[snIdx][field] = val;
        setItems(newItems);
    };

    const handleQuantityChange = (index, value) => {
        const newItems = [...items];
        const qty = parseInt(value) || 0;
        newItems[index].verifiedQty = qty;
        newItems[index].status = qty === newItems[index].purchaseQty ? "Verified" : (qty > newItems[index].purchaseQty ? "Over-Received" : "Pending");
        setItems(newItems);
    };

    const handleSubmit = async () => {
        const actualGrandTotal = items.reduce((acc, item) => {
            if (item.isSerialized) {
                return acc + item.serializedItems.reduce((s, unit) => s + Number(unit.unitCost || 0), 0);
            }
            return acc + (item.verifiedQty * item.unitCost);
        }, 0);

        setIsSubmitting(true);
        try {
            const payload = {
                actual_grand_total: actualGrandTotal,
                verification_notes: verificationNotes,
                supplier_bill_no: supplierBillNo,
                bill_proof: billProof?.imageUrl,
                verifiedItems: items
            };
            await apiService.post(`/purchase/verify/${purchase?._id}`, payload);
            toast.success("GRN Verified & Stock Updated.");
            onSuccess(items);
            onHide();
        } catch (error) {
            toast.error(error.response?.data?.message || "Verification Failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    const activeItem = activeScanIdx !== null ? items[activeScanIdx] : null;

    // --- RENDER ---
    return (
        <Modal show={show} onHide={onHide} fullscreen className="p-0">
            {/* 1. ENTERPRISE HEADER */}
            <Modal.Header className="border-bottom-0 text-white" style={{ backgroundColor: BRAND_COLOR }}>
                <div className="container-fluid d-flex justify-content-between align-items-center px-4">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-white bg-opacity-10 p-2 rounded">
                            <FaBoxOpen size={24} />
                        </div>
                        <div>
                            <div className="fw-bold fs-5">GRN Verification</div>
                            <div className="x-small opacity-75 font-monospace">REF: {purchase?.referenceNumber}</div>
                        </div>
                    </div>

                    {/* HERO SCANNER INPUT */}
                    <div className="flex-grow-1 mx-5" style={{ maxWidth: '600px' }}>
                        <div className="d-flex bg-white rounded-3 overflow-hidden p-1 gap-2 shadow-lg">
                            <div className={`d-flex align-items-center px-3 rounded fw-bold border ${activeItem ? 'bg-primary text-white border-primary' : 'bg-light text-muted border-secondary-subtle'}`} style={{ minWidth: '160px' }}>
                                <BiBarcodeReader className="me-2" size={18} />
                                <span className="small text-truncate" style={{ maxWidth: '130px' }}>{activeItem ? "Scanning..." : "Select Item"}</span>
                            </div>
                            <Form.Control
                                ref={scanInputRef}
                                type="text"
                                placeholder={activeItem ? `Scan IMEI for ${activeItem.itemName}...` : "Click an item below to unlock scanner"}
                                className="border-0 shadow-none fw-bold text-dark fs-6"
                                value={scanInput}
                                onChange={e => setScanInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleGlobalScan(e)}
                                disabled={activeScanIdx === null}
                                autoFocus
                                style={{ height: '40px' }}
                            />
                            <Button variant="primary" className="px-4 fw-bold" onClick={handleGlobalScan} disabled={!activeItem}>
                                ENTER
                            </Button>
                        </div>
                    </div>

                    <Button variant="white" className="bg-opacity-10 text-white border-white border-opacity-25" onClick={onHide}>
                        Save Draft & Exit
                    </Button>
                </div>
            </Modal.Header>

            {/* 2. PROGRESS BAR */}
            <div className="bg-white px-5 py-3 border-bottom shadow-sm d-flex align-items-center gap-4 sticky-top" style={{ zIndex: 1020, top: 0 }}>
                <span className="small fw-bold text-dark text-uppercase">Verification Progress</span>
                <ProgressBar now={stats.progress} variant={stats.progress === 100 ? "success" : "primary"} className="flex-grow-1" style={{ height: '10px' }} />
                <Badge bg={stats.progress === 100 ? "success" : "secondary"} className="px-3 py-2 fs-6">
                    {stats.verifiedItems} / {stats.totalItems} Items
                </Badge>
            </div>

            <Modal.Body className="bg-light p-4">
                <Row className="h-100 g-4">
                    {/* LEFT COLUMN: ITEM LIST */}
                    <Col lg={8} className="h-100 overflow-auto pb-5">
                        {items.map((item, idx) => {
                            const isComplete = item.verifiedQty >= item.purchaseQty;
                            const isActive = activeScanIdx === idx;

                            // Check Category for Battery Logic
                            const isDevice = item.category === 'Device' || item.category === 'Smartphones';

                            return (
                                <Card
                                    key={idx}
                                    className={`mb-4 border-0 shadow-sm transition-all cursor-pointer ${isActive ? 'ring-2' : ''}`}
                                    style={{
                                        outline: isActive ? '3px solid #0d6efd' : '1px solid #e2e8f0',
                                        opacity: (isComplete && !isActive) ? 0.6 : 1
                                    }}
                                    onClick={() => setActiveScanIdx(idx)}
                                >
                                    {/* Item Header */}
                                    <div className="p-3 d-flex align-items-center justify-content-between bg-white border-bottom">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className={`p-3 rounded-3 fw-bold fs-5 d-flex align-items-center justify-content-center ${isComplete ? 'bg-success text-white' : 'bg-light text-dark'}`} style={{ width: '50px', height: '50px' }}>
                                                {isComplete ? <FaCheck /> : (idx + 1)}
                                            </div>
                                            <div>
                                                {/* FIXED: VISIBLE ITEM NAME */}
                                                <h6 className="fw-bold mb-1 text-dark fs-6">
                                                    {item.itemName}
                                                </h6>

                                                <div className="d-flex gap-2 align-items-center">
                                                    {item.isSerialized ?
                                                        <Badge bg="dark" className="x-small">SERIALIZED</Badge> :
                                                        <Badge bg="secondary" className="x-small">BULK</Badge>
                                                    }
                                                    {/* Variant Attributes Chips */}
                                                    {item.variantAttributes?.map((attr, ai) => (
                                                        <Badge key={ai} bg="info" className="bg-opacity-10 text-info border border-info x-small">
                                                            {attr.value}
                                                        </Badge>
                                                    ))}
                                                    <span className="text-muted x-small ms-2 border-start ps-2">Cost: {formatCurrency(item.unitCost)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <div className="h3 mb-0 fw-bold font-monospace">
                                                <span className={isComplete ? "text-success" : "text-primary"}>{item.verifiedQty}</span>
                                                <span className="text-muted fs-5"> / {item.purchaseQty}</span>
                                            </div>
                                            <div className="x-small text-muted fw-bold text-uppercase">Units Verified</div>
                                        </div>
                                    </div>

                                    {/* Expanded Scanning Area */}
                                    {(isActive || item.verifiedQty > 0) && (
                                        <div className="p-3 bg-light">
                                            {item.isSerialized ? (
                                                <div className="table-responsive bg-white rounded border shadow-sm">
                                                    <Table className="mb-0 align-middle">
                                                        <thead className="bg-light x-small text-dark text-uppercase fw-bold border-bottom">
                                                            <tr>
                                                                <th className="ps-3 py-3">Serial Number / IMEI</th>
                                                                <th>Condition</th>
                                                                {isDevice && <th className="text-center">Battery</th>}
                                                                <th className="text-end pe-3">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {item.serializedItems.map((sn, sIdx) => (
                                                                <tr key={sIdx}>
                                                                    <td className="ps-3 py-2">
                                                                        <Form.Control
                                                                            type="text"
                                                                            value={sn.serialNumber}
                                                                            onChange={e => handleUnitChange(idx, sIdx, 'serialNumber', e.target.value)}
                                                                            className="border border-secondary-subtle font-monospace fw-bold text-dark bg-white shadow-none"
                                                                            placeholder="Scan/Type..."
                                                                        />
                                                                    </td>
                                                                    <td className="py-2">
                                                                        <Form.Select
                                                                            value={sn.condition}
                                                                            onChange={e => handleUnitChange(idx, sIdx, 'condition', e.target.value)}
                                                                            className="border border-secondary-subtle bg-white text-dark small"
                                                                        >
                                                                            {CONDITION_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                                                                        </Form.Select>
                                                                    </td>

                                                                    {/* FIXED: BATTERY HEALTH INPUT */}
                                                                    {isDevice && (
                                                                        <td className="text-center py-2" style={{ minWidth: '120px' }}>
                                                                            <InputGroup size="sm">
                                                                                <Form.Control
                                                                                    type="number"
                                                                                    value={sn.batteryHealth}
                                                                                    onChange={e => handleUnitChange(idx, sIdx, 'batteryHealth', e.target.value)}
                                                                                    className="text-center fw-bold border border-secondary text-dark bg-white"
                                                                                />
                                                                                <InputGroup.Text className="bg-light text-muted"><FaBatteryFull /></InputGroup.Text>
                                                                            </InputGroup>
                                                                        </td>
                                                                    )}

                                                                    <td className="text-end pe-3 py-2">
                                                                        <Button variant="light" size="sm" className="text-danger border-0" onClick={() => handleRemoveSerial(idx, sIdx)}>
                                                                            <FaTimes />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {/* Manual Add Button */}
                                                            <tr>
                                                                <td colSpan="100%" className="text-center p-2 bg-light">
                                                                    <Button variant="link" size="sm" className="text-decoration-none fw-bold" onClick={() => handleAddSerial(idx)}>
                                                                        <FaPlus className="me-1" /> Add Manual Line
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="p-3 bg-white border rounded d-flex align-items-center gap-3">
                                                    <div className="fw-bold text-muted">Bulk Quantity Check:</div>
                                                    <InputGroup style={{ maxWidth: '200px' }}>
                                                        <Form.Control
                                                            type="number"
                                                            value={item.verifiedQty}
                                                            onChange={e => handleQuantityChange(idx, e.target.value)}
                                                            className="fw-bold text-center border-secondary"
                                                        />
                                                        <Button variant="outline-success" onClick={() => handleQuantityChange(idx, item.purchaseQty)}>Match All</Button>
                                                    </InputGroup>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </Col>

                    {/* RIGHT COLUMN: SUMMARY & SUBMIT */}
                    <Col lg={4}>
                        <div className="sticky-top" style={{ top: '1rem' }}>
                            <Card className="border-0 shadow-sm mb-3">
                                <Card.Header className="bg-white fw-bold py-3 border-bottom text-uppercase small text-muted">
                                    GRN Finalization
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">PO Expected Value</span>
                                            <span className="fw-bold text-dark">{formatCurrency(purchase?.grand_total || 0)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-3 pb-3 border-bottom">
                                            <span className="text-dark fw-bold">Verified Value</span>
                                            <span className="fw-bold text-success fs-5">
                                                {formatCurrency(items.reduce((acc, item) => {
                                                    if (item.isSerialized) return acc + item.serializedItems.reduce((s, unit) => s + Number(unit.unitCost || 0), 0);
                                                    return acc + (item.verifiedQty * item.unitCost);
                                                }, 0))}
                                            </span>
                                        </div>
                                    </div>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="x-small fw-bold text-uppercase text-muted">Supplier Bill #</Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="border-secondary-subtle text-dark fw-bold"
                                            value={supplierBillNo}
                                            onChange={e => setSupplierBillNo(e.target.value)}
                                        />
                                    </Form.Group>

                                    <div className="mb-4">
                                        <ImageUploader
                                            label="Upload Bill Proof"
                                            value={billProof}
                                            onChange={(val) => setBillProof(val)}
                                        />
                                    </div>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="x-small fw-bold text-uppercase text-muted">Verification Notes</Form.Label>
                                        <Form.Control
                                            as="textarea" rows={3}
                                            className="border-secondary-subtle bg-white small"
                                            placeholder="Notes on damages, shorts, or price changes..."
                                            value={verificationNotes}
                                            onChange={e => setVerificationNotes(e.target.value)}
                                        />
                                    </Form.Group>

                                    {/* STATUS ALERTS */}
                                    {stats.progress < 100 ? (
                                        <Alert variant="warning" className="border-warning border-opacity-50 text-dark d-flex gap-3 align-items-start">
                                            <FaExclamationTriangle className="mt-1 text-warning" size={18} />
                                            <div>
                                                <strong>Partial Delivery</strong>
                                                <div className="small opacity-75">You are receiving less than ordered. The PO will stay open for the balance.</div>
                                            </div>
                                        </Alert>
                                    ) : (
                                        <Alert variant="success" className="border-success border-opacity-50 text-dark d-flex gap-3 align-items-start">
                                            <FaCheck className="mt-1 text-success" size={18} />
                                            <div>
                                                <strong>Full Delivery</strong>
                                                <div className="small opacity-75">All items verified. PO will be marked as Completed.</div>
                                            </div>
                                        </Alert>
                                    )}

                                    <Button
                                        variant="primary"
                                        size="lg"
                                        className="w-100 fw-bold shadow-sm py-3"
                                        style={{ backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <span className="spinner-border spinner-border-sm me-2" /> : <FaCube className="me-2" />}
                                        COMMIT TO STOCK
                                    </Button>
                                </Card.Body>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <style>{`
                .ring-2 { box-shadow: 0 0 0 2px #0d6efd; }
                .transition-all { transition: all 0.2s ease-in-out; }
            `}</style>
        </Modal>
    );
};

export default PurchaseVerification;